import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Languages, Pencil, Plus, Split, Trash2 } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { languageLabel } from '@/shared/lib/language'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { SurveyInstrumentDto, SurveyQuestionDto } from '@/shared/api/types'
import {
  createNextVersion, deleteQuestion, deleteSkipRule, getInstrument, getSkipLogicVocabulary,
  getVocabulary, listPolls, listVersions, publishVersion, reorderQuestions,
} from './api'
import { PollSelect } from './PollSelect'
import { Pill, count, questionTypeLabel } from './vocab'
import { evaluateSkips, type AnswerMap } from './skipRules'
import {
  PublishedOffer, QuestionDialog, publishedRefusal, type VersionOffer,
} from './QuestionDialog'
import { BlockDialog } from './BlockDialog'
import { SkipRuleDialog } from './SkipRuleDialog'
import { ConsentScriptDialog } from './ConsentScriptDialog'
import { TranslationDialog } from './TranslationDialog'

/**
 * The instrument, its versions, and a preview that runs the real branch logic.
 *
 * ## The preview is the point
 *
 * It evaluates the SAME rules the server will validate against — same operators,
 * same actions, same forward-only ordering — so an interviewer never meets a
 * branch the designer did not see. PollSphere has a `branchRules` column with
 * zero readers anywhere in its source: its `required` flag is decorative and its
 * skip rate is uncomputable.
 *
 * Tick answers on the left and the outline dims what the branch hides. That is
 * the whole feature: it is not a form, it is a demonstration. Every edit made on
 * this screen feeds straight back into it, so a designer meets their own branch
 * before an interviewer does.
 *
 * ## Every write returns the whole document, and the whole document is the state
 *
 * Adding a question renumbers positions; deleting one can invalidate a rule that
 * pointed at it; an option code can collide. The server knows all of that and
 * the client knows none of it, so nothing here patches local state from the
 * request it sent — `applyDoc` takes what came back, and the two routes that
 * answer 204 (delete, translations, consent) are followed by a re-read rather
 * than by a guess.
 *
 * ## A published version is read-only, and every control still works
 *
 * Not one editing control is hidden or disabled when the version is published.
 * Clicking one produces the server's 409 `survey/version-published` and the
 * offer that answers it — "create version N+1", which copies the instrument and
 * leaves every collected interview pointing at version N. That is rule 3: a
 * hidden control cannot be told apart from a broken one, and "I do not know why
 * nothing happens" is the failure this design exists to prevent.
 */
export function BuilderPage({ embeddedPollId }: {
  /**
   * Set when this is the configurator's Questions step rather than its own route.
   *
   * The ONLY thing it changes is the chrome: the page title and the poll picker
   * are suppressed, because the wizard already carries both above it and two
   * poll pickers on one screen is a way to have them disagree. Everything else —
   * the version selector, the preview, the published refusal and the offer that
   * answers it — is identical, because it is the same component. A
   * wizard-flavoured second editor would be two editors for one instrument.
   */
  embeddedPollId?: string
} = {}) {
  const canRead = useCan('survey:read')
  const canUpdate = useCan('survey:update')
  const canPublish = useCan('survey:publish')
  const [params, setParams] = useSearchParams()
  // The embedded poll WINS over the query string. The configurator writes `poll`
  // itself, so the two agree today — but a stale `?poll=` left by another screen
  // must not out-vote the poll the wizard is demonstrably editing.
  const pollId = embeddedPollId ?? params.get('poll')
  const versionParam = params.get('version')
  const [answers, setAnswers] = useState<AnswerMap>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The server's own words when a write hit a published version. Never a red strip. */
  const [frozen, setFrozen] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const [questionEdit, setQuestionEdit] = useState<{ question: SurveyQuestionDto | null } | null>(null)
  const [blockOpen, setBlockOpen] = useState(false)
  const [skipEdit, setSkipEdit] = useState<{ sourceQuestionId: string | null } | null>(null)
  const [consentOpen, setConsentOpen] = useState(false)
  const [translateId, setTranslateId] = useState<string | null>(null)

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const versions = useLoad(
    () => (pollId ? listVersions(pollId) : Promise.resolve([])), [pollId])
  // Rule 4: every picker on this screen is built from these two, never from a
  // list written here. A question type or an operator added server-side shows up
  // without a release; one removed stops being offered for the same reason.
  const vocabulary = useLoad(() => getVocabulary(), [])
  const skipVocabulary = useLoad(() => getSkipLogicVocabulary(), [])

  const versionId = versionParam
    ?? (versions.data ?? []).slice().sort((a, b) => b.versionNo - a.versionNo)[0]?.id
    ?? null

  const loaded = useLoad(
    () => (versionId ? getInstrument(versionId) : Promise.resolve(null)), [versionId])

  /**
   * The document as the last write left it.
   *
   * Keyed by version id so switching version cannot show the previous one's
   * questions for a frame: a stale override under a fresh version number is the
   * exact confusion this screen is meant to remove.
   */
  const [written, setWritten] = useState<{ versionId: string; doc: SurveyInstrumentDto } | null>(null)
  /**
   * The loaded document only counts while it IS the selected version.
   *
   * `useLoad` keeps the previous result until the next one lands, so without this
   * check the moment after "create version N+1" renders v3's questions under a
   * banner reading v4 — the one thing this screen cannot afford to be wrong
   * about. Falling to null instead puts the loading block there for a frame.
   */
  const fresh = loaded.data && loaded.data.version.id === versionId ? loaded.data : null
  const data = written && written.versionId === versionId ? written.doc : fresh

  const applyDoc = (doc: SurveyInstrumentDto) =>
    setWritten({ versionId: doc.version.id, doc })

  /** For the two routes that answer 204. Re-read rather than reconstruct. */
  const refetch = async () => {
    if (versionId) applyDoc(await getInstrument(versionId))
  }

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key === 'poll') next.delete('version')
    setParams(next, { replace: true })
    setError(null)
    setFrozen(null)
  }

  const outcome = useMemo(
    () => (data ? evaluateSkips(data.questions, data.skipRules, answers) : null),
    [data, answers])

  const ordered = useMemo(
    () => (data ? [...data.questions].sort((a, b) => a.position - b.position) : []),
    [data])

  if (!canRead) return <Forbidden what="the instrument builder" />

  const published = data?.version.stateCode === 'PUBLISHED'
  const nextVersionNo = (data?.version.versionNo ?? 0) + 1

  async function nextVersion() {
    if (!pollId) return
    const note = window.prompt('What changed? This is the version note.')
    if (note === null) return
    setBusy(true)
    setError(null)
    try {
      const created = await createNextVersion(pollId, note.trim() || 'Copy of the previous version')
      versions.reload()
      setFrozen(null)
      set('version', created.id)
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Every write on this page, including the ones a dialog does not own.
   *
   * The 409 goes to `frozen` and the offer, not to the error strip. Everything
   * else — a 400 that is not a permutation, a 404 on a row somebody else deleted
   * — is the server's sentence in the strip, because those are things the author
   * has to know about rather than act on.
   */
  async function runWrite(fn: () => Promise<SurveyInstrumentDto | void>) {
    if (!versionId) return
    setBusy(true)
    setError(null)
    setFrozen(null)
    try {
      const next = await fn()
      if (next) applyDoc(next)
      else await refetch()
    } catch (e) {
      const refusal = publishedRefusal(e)
      if (refusal) setFrozen(refusal)
      else setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  async function publish() {
    if (!versionId) return
    setBusy(true)
    setError(null)
    try {
      await publishVersion(versionId)
      versions.reload()
      setWritten(null)
      loaded.reload()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Up and down, and deliberately not drag-and-drop.
   *
   * Drag-and-drop for a list is a large amount of accessibility work — pointer
   * capture, a live region announcing every move, an equivalent keyboard grammar
   * — and when it is skipped, which it usually is, keyboard and screen-reader
   * users get nothing at all from it. Two buttons are the keyboard interface, so
   * there is only one interface to build and one to test. Thirty rows is the
   * usual size of an instrument; the cost of the extra clicks is small and it is
   * paid by everybody equally.
   *
   * The whole permutation is sent because that is what the endpoint takes. A
   * list that is not one comes back 400 naming what is missing — unreachable
   * from these buttons, and rendered anyway, because "unreachable" is a claim
   * about today's UI and not about the request that arrives.
   */
  function moveQuestion(index: number, by: -1 | 1) {
    const to = index + by
    const from = ordered[index]
    const swap = ordered[to]
    if (!from || !swap || !versionId) return
    const ids = ordered.map((q) => q.id)
    ids[index] = swap.id
    ids[to] = from.id
    void runWrite(() => reorderQuestions(versionId, ids))
  }

  const offer: VersionOffer = {
    nextVersionNo,
    canUpdate,
    busy,
    onCreate: () => { void nextVersion() },
  }

  const translating = translateId
    ? (data?.questions.find((q) => q.id === translateId) ?? null)
    : null

  const versionPicker = (versions.data ?? []).length > 0 && (
    <label className="flex items-center gap-1.5 text-erp-xs">
      Version
      <select className="erp-input h-9" value={versionId ?? ''}
        onChange={(e) => set('version', e.target.value)}>
        {(versions.data ?? []).slice().sort((a, b) => b.versionNo - a.versionNo)
          .map((v) => (
            <option key={v.id} value={v.id}>v{v.versionNo} · {v.stateCode}</option>
          ))}
      </select>
    </label>
  )

  return (
    <div className="space-y-4">
      {embeddedPollId ? (
        // The wizard's own header names the poll and the step, so this contributes
        // the one control it does not have: which version is being edited.
        versionPicker && <div className="flex items-center justify-end">{versionPicker}</div>
      ) : (
        <SectionHeader
          title="Builder"
          description="Questions, branches and versions. The preview runs the same rules the server validates against."
          right={(
            <div className="flex items-center gap-2">
              <PollSelect polls={polls.data?.content ?? []} value={pollId}
                onChange={(id) => set('poll', id)} />
              {versionPicker}
            </div>
          )}
        />
      )}

      <ErrorStrip message={error ?? loaded.error} />

      {!pollId ? (
        <Note tone="info">Pick a poll to open its instrument.</Note>
      ) : loaded.loading && !data ? <LoadingBlock label="Loading the instrument…" /> : data && (
        <>
          {/*
            The server's refusal wins over our standing banner: it names the
            version numbers and says what happens to the interviews already
            collected, which is what somebody who just clicked Delete needs.
          */}
          {frozen ? (
            <PublishedOffer sentence={frozen} offer={offer} />
          ) : published && (
            <Note tone="info">
              Version {data.version.versionNo} is published and cannot be edited — the
              interviews already collected point at it, and editing it would change the wording
              somebody answered. Every control below is still live: clicking one asks the server,
              and the server answers with this same offer. Create version{' '}
              {nextVersionNo}: it copies this instrument and re-points every branch, and the
              existing answers stay where they are.
              {canUpdate && (
                <button type="button" className="erp-btn-ghost h-8 ml-2 text-erp-sm text-chocolate"
                  disabled={busy} onClick={nextVersion}>
                  Create version {nextVersionNo}
                </button>
              )}
            </Note>
          )}

          {!published && canPublish && (
            <div className="flex items-center gap-2">
              <button type="button" className="erp-btn-primary h-9 text-erp-sm"
                disabled={busy} onClick={publish}>
                {busy ? 'Publishing…' : `Publish version ${data.version.versionNo}`}
              </button>
              <span className="text-erp-xs text-muted-foreground">
                Publishing freezes the wording and stamps a checksum. Nothing collects against a
                draft.
              </span>
            </div>
          )}

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-7">
              <Outline
                instrument={data}
                ordered={ordered}
                hiddenBy={outcome?.hiddenBy ?? {}}
                canUpdate={canUpdate}
                busy={busy}
                confirmDelete={confirmDelete}
                onConfirmDelete={setConfirmDelete}
                onAddQuestion={() => setQuestionEdit({ question: null })}
                onAddBlock={() => setBlockOpen(true)}
                onEdit={(q) => setQuestionEdit({ question: q })}
                onTranslate={(q) => setTranslateId(q.id)}
                onBranchFrom={(q) => setSkipEdit({ sourceQuestionId: q.id })}
                onMove={moveQuestion}
                onDelete={(q) => {
                  setConfirmDelete(null)
                  void runWrite(() => deleteQuestion(q.id))
                }}
              />
            </div>

            <div className="col-span-12 lg:col-span-5 space-y-4">
              <Panel
                title="Preview"
                description="Answer as a respondent would. The outline dims what the branches hide."
              >
                <div className="space-y-3">
                  {ordered.filter((q) => q.options.length > 0).map((q) => (
                    <PreviewQuestion
                      key={q.id}
                      question={q}
                      value={answers[q.id] ?? null}
                      disabled={!!outcome?.hiddenBy[q.id]}
                      onChange={(code) => setAnswers((prev) => ({ ...prev, [q.id]: code }))}
                    />
                  ))}
                  {Object.keys(answers).length > 0 && (
                    <button type="button" className="erp-btn-ghost h-8 text-erp-sm text-chocolate"
                      onClick={() => setAnswers({})}>
                      Clear preview answers
                    </button>
                  )}
                </div>
                {ordered.length > 0 && ordered.every((q) => q.options.length === 0) && (
                  <Note tone="info">
                    Nothing here is answered from a card, so there is nothing to tick. The preview
                    follows option answers only — a rule reading a number or a date is evaluated
                    by the server against the real value.
                  </Note>
                )}
                {outcome?.ended && (
                  <div className="mt-3">
                    <Note tone="info">
                      A branch ends the interview here. Everything after it is hidden, and on
                      submission those questions are recorded as skipped-by-rule rather than
                      left blank.
                    </Note>
                  </div>
                )}
              </Panel>

              <Panel
                title="Branches"
                description="Evaluated in position order. A rule may only point forward."
                right={canUpdate && (
                  <button type="button" className="erp-btn h-8 text-erp-xs"
                    onClick={() => setSkipEdit({ sourceQuestionId: null })}>
                    <Plus className="h-3 w-3" /> Add branch
                  </button>
                )}
              >
                {data.skipRules.length === 0 ? (
                  <Note tone="info">No branches. Every respondent sees every question.</Note>
                ) : (
                  <ul className="space-y-1.5 text-erp-sm">
                    {[...data.skipRules].sort((a, b) => a.position - b.position).map((rule) => (
                      <li key={rule.id}
                        className="rounded border border-border p-2 flex items-start gap-2">
                        <span className="min-w-0 flex-1">
                          <span className="font-mono text-erp-xs text-muted-foreground">
                            {rule.sourceQuestionCode ?? '?'}
                          </span>
                          {' '}{rule.operatorCode}{' '}
                          <span className="font-mono text-erp-xs">{rule.valueJson ?? ''}</span>
                          {' → '}
                          <span className="text-chocolate">{rule.actionCode}</span>
                          {rule.targetQuestionCode && (
                            <span className="font-mono text-erp-xs ml-1">
                              {rule.targetQuestionCode}
                            </span>
                          )}
                          {!rule.targetQuestionCode && rule.targetBlockId && (
                            <span className="font-mono text-erp-xs ml-1">
                              block {data.blocks.find((b) => b.id === rule.targetBlockId)?.code
                                ?? rule.targetBlockId.slice(0, 8)}
                            </span>
                          )}
                        </span>
                        {canUpdate && (confirmDelete === rule.id ? (
                          <span className="inline-flex items-center gap-1.5 text-erp-xs shrink-0">
                            <span className="text-muted-foreground">Delete?</span>
                            <button type="button"
                              className="erp-btn erp-btn-ghost h-7 text-erp-xs text-red-700"
                              onClick={() => {
                                setConfirmDelete(null)
                                void runWrite(() => deleteSkipRule(rule.id))
                              }}>Yes</button>
                            <button type="button" className="erp-btn erp-btn-ghost h-7 text-erp-xs"
                              onClick={() => setConfirmDelete(null)}>No</button>
                          </span>
                        ) : (
                          <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0 shrink-0"
                            aria-label={`Delete branch on ${rule.sourceQuestionCode ?? 'a question'}`}
                            onClick={() => setConfirmDelete(rule.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ))}
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <ConsentPanel instrument={data} canUpdate={canUpdate}
                onEdit={() => setConsentOpen(true)} />

              <LanguagePanel instrument={data} />
            </div>
          </div>

          {questionEdit && versionId && (
            <QuestionDialog
              open
              versionId={versionId}
              pollId={pollId}
              question={questionEdit.question}
              blocks={data.blocks}
              questionCount={data.questions.length}
              vocabulary={vocabulary.data}
              offer={offer}
              onClose={() => setQuestionEdit(null)}
              onSaved={(doc) => { applyDoc(doc); setQuestionEdit(null) }}
            />
          )}

          {blockOpen && versionId && (
            <BlockDialog
              open
              versionId={versionId}
              blockCount={data.blocks.length}
              offer={offer}
              onClose={() => setBlockOpen(false)}
              onSaved={(doc) => { applyDoc(doc); setBlockOpen(false) }}
            />
          )}

          {skipEdit && versionId && (
            <SkipRuleDialog
              open
              versionId={versionId}
              instrument={data}
              vocabulary={skipVocabulary.data}
              initialSourceQuestionId={skipEdit.sourceQuestionId}
              offer={offer}
              onClose={() => setSkipEdit(null)}
              onSaved={(doc) => { applyDoc(doc); setSkipEdit(null) }}
            />
          )}

          {consentOpen && (
            <ConsentScriptDialog
              open
              instrument={data}
              onClose={() => setConsentOpen(false)}
              // 204, so there is nothing to apply — the document is re-read.
              onSaved={() => { void refetch() }}
            />
          )}

          {translating && versionId && (
            <TranslationDialog
              open
              versionId={versionId}
              question={translating}
              instrument={data}
              offer={offer}
              onClose={() => setTranslateId(null)}
              onSaved={() => { void refetch() }}
            />
          )}
        </>
      )}
    </div>
  )
}

/**
 * The instrument as an interviewer walks it.
 *
 * Questions are listed in position order and a block header is emitted wherever
 * the block changes, rather than the questions being bucketed by block first.
 * Bucketing reads more tidily and would be a lie: the order on this screen has
 * to be the order they are asked in, and a question sitting between two blocks
 * is a fact about the instrument that grouping would hide.
 */
function Outline({
  instrument, ordered, hiddenBy, canUpdate, busy, confirmDelete, onConfirmDelete,
  onAddQuestion, onAddBlock, onEdit, onTranslate, onBranchFrom, onMove, onDelete,
}: {
  instrument: SurveyInstrumentDto
  ordered: SurveyQuestionDto[]
  hiddenBy: Record<string, string>
  canUpdate: boolean
  busy: boolean
  confirmDelete: string | null
  onConfirmDelete: (id: string | null) => void
  onAddQuestion: () => void
  onAddBlock: () => void
  onEdit: (q: SurveyQuestionDto) => void
  onTranslate: (q: SurveyQuestionDto) => void
  onBranchFrom: (q: SurveyQuestionDto) => void
  onMove: (index: number, by: -1 | 1) => void
  onDelete: (q: SurveyQuestionDto) => void
}) {
  const blockOf = (id: string | null) => instrument.blocks.find((b) => b.id === id) ?? null
  let lastBlockId: string | null | undefined

  return (
    <Panel
      title={`Instrument · v${instrument.version.versionNo}`}
      description={instrument.version.publishedAt
        ? `Published ${fmtDateTime(instrument.version.publishedAt)} · checksum ${(instrument.version.checksum ?? '').slice(0, 12)}`
        : `${instrument.questions.length} question${instrument.questions.length === 1 ? '' : 's'}, not yet published`}
      right={canUpdate && (
        <>
          <button type="button" className="erp-btn h-8 text-erp-xs" onClick={onAddBlock}>
            <Plus className="h-3 w-3" /> Block
          </button>
          <button type="button" className="erp-btn h-8 text-erp-xs" onClick={onAddQuestion}>
            <Plus className="h-3 w-3" /> Question
          </button>
        </>
      )}
    >
      <ol className="space-y-1.5">
        {ordered.map((q, i) => {
          const hidden = hiddenBy[q.id]
          const block = blockOf(q.blockId)
          const newBlock = q.blockId !== lastBlockId
          lastBlockId = q.blockId
          const substantive = q.options.filter((o) => o.kindCode === 'SUBSTANTIVE').length
          const missingLanguages = instrument.languages
            .filter((l) => !q.translations[l])

          return (
            <li key={q.id}>
              {newBlock && (
                <div className="pt-2 pb-1 flex items-baseline gap-2">
                  <span className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {block ? `${block.code}${block.title ? ` · ${block.title}` : ''}` : 'No block'}
                  </span>
                  {block?.description && (
                    <span className="text-erp-xs text-muted-foreground truncate">
                      {block.description}
                    </span>
                  )}
                </div>
              )}
              <div className={`rounded border p-3 ${hidden
                ? 'border-dashed border-border bg-muted/20 opacity-60'
                : 'border-border bg-background'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-erp-sm">
                      <span className="font-mono text-erp-xs text-muted-foreground mr-2 tabular-nums">
                        {q.position}. {q.code}
                      </span>
                      {q.text}
                    </p>
                    {q.helpText && (
                      <p className="text-erp-xs text-muted-foreground mt-0.5 italic">
                        {q.helpText}
                      </p>
                    )}
                    <p className="text-erp-xs text-muted-foreground mt-1 flex flex-wrap gap-1.5">
                      <Pill>{questionTypeLabel(q.typeCode)}</Pill>
                      {q.required && <Pill>required</Pill>}
                      {q.randomiseOptions && <Pill>randomised</Pill>}
                      {q.options.length > 0 && (
                        <Pill>
                          {count(substantive)} of {count(q.options.length)} substantive
                        </Pill>
                      )}
                      {q.scaleMin !== null && q.scaleMax !== null && (
                        <Pill>scale {q.scaleMin}–{q.scaleMax}</Pill>
                      )}
                      {q.maxSelections !== null && <Pill>up to {q.maxSelections}</Pill>}
                      {instrument.languages.length > 0 && (
                        <Pill>
                          {missingLanguages.length === 0
                            ? 'translated'
                            : `missing ${missingLanguages.map(languageLabel).join(', ')}`}
                        </Pill>
                      )}
                      {q.linkedEntity && (
                        <Pill>
                          {q.linkedEntity.resolved
                            ? `${q.linkedEntity.typeCode}: ${q.linkedEntity.label}`
                            : `${q.linkedEntity.typeCode}: unresolved`}
                        </Pill>
                      )}
                    </p>
                    {q.options.length > 0 && substantive < q.options.length && (
                      <p className="text-erp-xs text-muted-foreground mt-1">
                        {q.options.filter((o) => o.kindCode !== 'SUBSTANTIVE')
                          .map((o) => `${o.code} (${o.kindCode})`).join(', ')}
                        {' '}sit outside the substantive base — reported as their own rows, not
                        folded into the denominator of a share.
                      </p>
                    )}
                  </div>

                  <div className="shrink-0 flex flex-col items-end gap-1">
                    {hidden && (
                      <span className="text-erp-xs text-muted-foreground">hidden by a branch</span>
                    )}
                    {canUpdate && (confirmDelete === q.id ? (
                      <span className="inline-flex items-center gap-1.5 text-erp-xs">
                        <span className="text-muted-foreground">Delete {q.code}?</span>
                        <button type="button"
                          className="erp-btn erp-btn-ghost h-7 text-erp-xs text-red-700"
                          disabled={busy} onClick={() => onDelete(q)}>Yes</button>
                        <button type="button" className="erp-btn erp-btn-ghost h-7 text-erp-xs"
                          onClick={() => onConfirmDelete(null)}>No</button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                          disabled={i === 0} aria-label={`Move ${q.code} earlier`}
                          onClick={() => onMove(i, -1)}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                          disabled={i === ordered.length - 1} aria-label={`Move ${q.code} later`}
                          onClick={() => onMove(i, 1)}>
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                          aria-label={`Branch from ${q.code}`} onClick={() => onBranchFrom(q)}>
                          <Split className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                          aria-label={`Translate ${q.code}`} onClick={() => onTranslate(q)}>
                          <Languages className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                          aria-label={`Edit ${q.code}`} onClick={() => onEdit(q)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {/*
                          Offered on a published version too. It comes back 409
                          with the offer attached, which is a better answer than a
                          button that is there but does nothing.
                        */}
                        <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                          aria-label={`Delete ${q.code}`} onClick={() => onConfirmDelete(q.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ol>

      {instrument.questions.length === 0 && (
        <Note tone="info">
          This version has no questions yet. An instrument with none cannot be
          published, and a poll cannot go live without a published one.
        </Note>
      )}

      {instrument.questions.length > 0 && (
        <p className="pt-3 text-erp-xs text-muted-foreground">
          Reordering renumbers every position server-side and the whole document comes back —
          which is also how a branch that would now point backwards is caught. Deleting a
          question takes the branches that read it or pointed at it with it, because a rule with
          half its ends missing is a branch that silently stopped working.
        </p>
      )}
    </Panel>
  )
}

/**
 * The consent script, and why the poll cannot go live without one.
 *
 * On the right column rather than buried in a settings screen because it is a
 * `→ LIVE` blocker and this is the only place in the product that can clear it.
 */
function ConsentPanel({ instrument, canUpdate, onEdit }: {
  instrument: SurveyInstrumentDto
  canUpdate: boolean
  onEdit: () => void
}) {
  const scripted = instrument.consentScripts.map((s) => s.languageCode)
  const missing = instrument.languages.filter((l) => !scripted.includes(l))
  const audioOn = instrument.audioRecordingMode !== 'OFF'
  const withoutDisclosure = instrument.consentScripts
    .filter((s) => !s.audioDisclosure).map((s) => s.languageCode)

  return (
    <Panel
      title="Consent script"
      description="Read out before the first question. Nothing else in the product can write one."
      right={canUpdate && (
        <button type="button" className="erp-btn h-8 text-erp-xs" onClick={onEdit}>
          <Pencil className="h-3 w-3" /> Edit
        </button>
      )}
    >
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5 text-erp-sm">
          <span className="text-muted-foreground text-erp-xs">Has a script:</span>
          {scripted.length === 0
            ? <span className="text-erp-sm text-muted-foreground">none</span>
            : scripted.map((l) => <Pill key={l}>{languageLabel(l)}</Pill>)}
        </div>
        {missing.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-erp-sm">
            <span className="text-muted-foreground text-erp-xs">Missing:</span>
            {missing.map((l) => <Pill key={l}>{languageLabel(l)}</Pill>)}
          </div>
        )}
        {instrument.consentRequired && scripted.length === 0 && (
          <Note tone="bad">
            This poll requires consent and has no script, so `→ LIVE` is refused. The refusal
            names this and there is nowhere else to answer it.
          </Note>
        )}
        {audioOn && withoutDisclosure.length > 0 && (
          <Note tone="bad">
            This poll records audio ({instrument.audioRecordingMode}), and no disclosure is
            written in {withoutDisclosure.join(', ')}. Consent to be interviewed is not consent
            to be recorded; the server refuses both the script and `→ LIVE` for that reason.
          </Note>
        )}
      </div>
    </Panel>
  )
}

/**
 * Which languages this instrument is actually written in.
 *
 * The counts are of questions carrying a translation, not of languages declared:
 * a language in the list with two of thirty questions translated is the failure
 * mode worth showing, because it is the one that looks like success.
 */
function LanguagePanel({ instrument }: { instrument: SurveyInstrumentDto }) {
  const total = instrument.questions.length

  return (
    <Panel
      title="Languages"
      description="An instrument fielded in Kiswahili that stores only the English wording cannot be audited — nobody can check what was actually asked."
    >
      {instrument.languages.length === 0 ? (
        <Note tone="info">
          No translations. Every question is asked in the wording above, and an interviewer
          working in another language is translating on the spot — which is wording nobody
          reviewed and nothing recorded.
        </Note>
      ) : (
        <ul className="space-y-1.5">
          {instrument.languages.map((l) => {
            const done = instrument.questions.filter((q) => q.translations[l]).length
            const options = instrument.questions.flatMap((q) => q.options)
            const optionsDone = options.filter((o) => o.translations[l]).length
            return (
              <li key={l} className="flex items-center justify-between gap-2 text-erp-sm">
                <span className="flex items-center gap-2">
                  <Pill>{languageLabel(l)}</Pill>
                  <span className="text-erp-xs text-muted-foreground">
                    {count(done)} of {count(total)} questions
                    {options.length > 0
                      && `, ${count(optionsDone)} of ${count(options.length)} options`}
                  </span>
                </span>
                {done < total && (
                  <span className="text-erp-xs text-muted-foreground shrink-0">
                    part-translated
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <p className="pt-2 text-erp-xs text-muted-foreground">
        A language appears here as soon as one translation is written in it. Translate a question
        from the outline; a half-translated language is not offered to an interviewer, so the
        work is only worth anything once a language is finished.
      </p>
    </Panel>
  )
}

function PreviewQuestion({ question, value, disabled, onChange }: {
  question: SurveyQuestionDto
  value: string | null
  disabled: boolean
  onChange: (code: string) => void
}) {
  return (
    <div className={`rounded border border-border p-3 ${disabled ? 'opacity-50' : ''}`}>
      <p className="text-erp-sm mb-2">{question.text}</p>
      <div className="flex flex-wrap gap-1.5">
        {question.options.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            className={`erp-btn-ghost h-8 text-erp-xs ${
              value === option.code ? 'bg-chocolate/10 text-chocolate' : ''}`}
            onClick={() => onChange(option.code)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
